import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { USER_STORE } from '@dxp/core/stores';
import {
    TelXLButtonComponent,
    TelXLLoadingComponent,
    TelXLPanelComponent,
    TelXLSearchComponent,
} from '@dxp/elements';
import { TemplateApiService } from '@dxp/core/api';
import { ITemplate, ITemplateWithContent } from '@dxp/core/models';
import { SortPipe, UnsafePipe } from '@dxp/core/pipes';
import { ViewportService } from '@dxp/core/services';
import { MediaGroup } from '@dxp/core/types';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs';

@Component({
    selector: 'telxl-shared-template-listing',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        SortPipe,
        UnsafePipe,
        TelXLPanelComponent,
        TelXLSearchComponent,
        TelXLButtonComponent,
        TelXLLoadingComponent,
    ],
    templateUrl: './template-listing-panel.component.html',
    styleUrl: './template-listing-panel.component.scss',
})
export class TelXLTemplateListingPanelComponent implements AfterViewInit {
    readonly #destroyRef = inject(DestroyRef);
    readonly #templateApiService = inject(TemplateApiService);
    readonly #userStore = inject(USER_STORE);
    readonly viewportService = inject(ViewportService);

    @ViewChild('templates', { static: false })
    templates!: ElementRef<HTMLDivElement>;

    mediaGroup = input.required<MediaGroup>();
    businessUnitId = input<string | null>(null);
    customerId = input.required<string>();
    conversationId = input.required<string>();
    height = input.required<string>();
    template = output<ITemplateWithContent | undefined>();

    readonly searchText = signal<string>('');
    readonly previewing = signal<ITemplate | null>(null);

    readonly loading = computed<boolean>(() => this.#templates() === undefined);
    readonly hasTemplates = computed<boolean>(
        () =>
            this.#templates() !== undefined &&
            (this.#templates() ?? []).length > 0,
    );

    readonly filteredTemplates = computed<ITemplate[]>(() => {
        const list = this.#templates() ?? [];
        const q = this.searchText().trim().toLowerCase();
        if (q.length === 0) return list;
        return list.filter(t => t.name.toLowerCase().includes(q));
    });

    readonly #templates = toSignal(
        combineLatest([
            toObservable(this.mediaGroup),
            toObservable(this.businessUnitId),
        ]).pipe(
            distinctUntilChanged(
                ([mgA, buA], [mgB, buB]) => mgA === mgB && buA === buB,
            ),
            switchMap(([mg, bu]) =>
                this.#templateApiService.getTemplates(mg, bu),
            ),
        ),
    );

    readonly #previewCache = signal<Map<string, string>>(
        new Map<string, string>(),
    );

    readonly #agentId = computed<string>(() => this.#userStore.user().id);

    readonly previewContent = toSignal(
        toObservable(this.previewing).pipe(
            filter(
                (template): template is ITemplate =>
                    !!template?.id && template.id.trim().length > 0,
            ),
            distinctUntilChanged(),
            switchMap(template => {
                const cached = this.#previewCache().get(template.id);
                if (cached !== undefined) {
                    return of(cached);
                }
                return this.#templateApiService
                    .getTemplateContent(
                        template.id,
                        this.conversationId(),
                        this.customerId(),
                        this.#agentId(),
                        this.mediaGroup(),
                    )
                    .pipe(
                        tap(content => {
                            this.#previewCache.update(old => {
                                const next = new Map(old);
                                next.set(template.id, content);
                                return next;
                            });
                        }),
                        catchError(() => of(null)),
                    );
            }),
        ),
    );

    readonly width = signal<number>(0);
    readonly content = computed<string>(() => this.previewContent() as string);

    readonly isBelow50PercentWidth = computed<boolean>(
        () =>
            Math.round(
                (this.width() / (this.viewportService.width() - 88)) * 100,
            ) < 50,
    );

    ngAfterViewInit() {
        const tryAttach = () => {
            const el = this.templates?.nativeElement;
            if (!el) {
                requestAnimationFrame(tryAttach);
                return;
            }
            const ro = new ResizeObserver(([entry]) =>
                this.width.set(entry.contentRect.width),
            );
            ro.observe(el);
            this.#destroyRef.onDestroy(() => ro.disconnect());
            this.width.set(el.getBoundingClientRect().width);
        };
        tryAttach();
    }

    searchChanged(searchText: string) {
        this.searchText.set(searchText);
    }

    onClose() {
        this.template.emit(undefined);
    }

    preview(template: ITemplate) {
        this.previewing.set(template);
    }

    use(template: ITemplate) {
        const cached = this.#previewCache().get(template.id);
        if (cached !== undefined) {
            this.template.emit({ ...template, content: cached });
            return;
        }

        this.#templateApiService
            .getTemplateContent(
                template.id,
                this.conversationId(),
                this.customerId(),
                this.#agentId(),
                this.mediaGroup(),
            )
            .pipe(
                take(1),
                tap(content => {
                    this.#previewCache.update(old => {
                        const next = new Map(old);
                        next.set(template.id, content ?? '');
                        return next;
                    });
                }),
                catchError(() => of(null)),
            )
            .subscribe(content => {
                if (content) {
                    this.template.emit({ ...template, content });
                } else {
                    this.template.emit(undefined);
                }
            });
    }
}
